import { ActionIcon, Box, Button, Checkbox, Group, Loader, rem, Stack, Text, Title, Tooltip, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMap } from "@mantine/hooks";
import { IconChevronRight } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useState } from "react";
import { StrictUndef } from "shared/api/httpClient";
import { NestedTopicTree } from "shared/components/organization/topics/TopicNode";
import { handleRoleAssign, UserSchema } from "shared/components/organization/users/data";
import { topicsQueryOptions } from "shared/graphql/topics";
import { useColorMode } from "shared/hooks/useColorMode";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { permissionCheck, usePermissions } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { graphql } from "src/gql";
import { FullUserFragment, Permission } from "src/gql/graphql";

const TopicNode = ({
    topic,
    checkedNodes,
    checkboxOnChange,
    inherited,
}: {
    topic: NestedTopicTree;
    checkedNodes: string[];
    checkboxOnChange: (topic: NestedTopicTree, checked: boolean) => void;
    inherited: boolean;
}) => {
    const theme = useMantineTheme();
    const { isDark } = useColorMode();

    const { permissions } = usePermissions();
    const {
        data: { rootTopic },
    } = useSuspenseQuery(topicsQueryOptions.rootTopic);

    const [opened, { toggle }] = useDisclosure(topic.id == rootTopic.id);

    const shouldShowExpandButton = topic.isCategory && (topic.children?.length ?? 0) > 0;
    const hasPermission = permissionCheck(permissions, Permission.TopicRolesManage, topic.id);

    const disabled = !hasPermission || inherited;

    return (
        <Box pos="relative">
            <Stack gap={0} style={{ display: "inline-flex" }} w="min-content">
                <Group gap={0} w="min-content" p="sm" pl={shouldShowExpandButton ? 0 : 28} wrap="nowrap" style={{ whiteSpace: "nowrap" }}>
                    <ActionIcon
                        display={shouldShowExpandButton ? undefined : "none"}
                        variant="transparent"
                        color="text"
                        onClick={toggle}
                        aria-label="Show or hide subtopics"
                    >
                        <IconChevronRight
                            size={16}
                            style={{ transition: "transform 0.5s", transform: opened ? "rotate(90deg)" : "rotate(0deg)" }}
                        />
                    </ActionIcon>
                    <Tooltip
                        disabled={!disabled}
                        label={
                            inherited
                                ? "Permissions are inherited from the role on one or more parent categories"
                                : "You do not have permission to assign roles to this topic/category"
                        }
                    >
                        <Checkbox
                            disabled={disabled}
                            checked={inherited || checkedNodes.includes(topic.id)}
                            onChange={(e) => checkboxOnChange(topic, e.target.checked)}
                            pr="sm"
                            aria-labelledby={`topic-label-${topic.id}`}
                        />
                    </Tooltip>
                    <Text id={`topic-label-${topic.id}`}>{topic.name}</Text>
                </Group>
                <Suspense fallback={<Loader />}>
                    {opened && (
                        <Stack
                            gap={0}
                            w="fit-content"
                            style={{
                                borderLeft: `var(--mantine-spacing-md) solid ${isDark ? theme.colors.dark[5] : theme.colors.gray[1]}`,
                                overflow: "auto",
                            }}
                        >
                            <Box>
                                {topic.children?.map((c) => (
                                    <TopicNode
                                        key={c.id}
                                        topic={c}
                                        checkedNodes={checkedNodes}
                                        checkboxOnChange={checkboxOnChange}
                                        inherited={inherited || checkedNodes.includes(topic.id)}
                                    />
                                ))}
                            </Box>
                        </Stack>
                    )}
                </Suspense>
            </Stack>
        </Box>
    );
};

export const TopicAssignPanel = ({ userIds, selectedRoleId }: { userIds: string[]; selectedRoleId: string }) => {
    const {
        data: { rootTopic },
    } = useSuspenseQuery(topicsQueryOptions.theWholeTree());

    const assignTopicRole = useGraphqlMutation(muts.assignTopicRole);

    const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[]>([]);

    return (
        <Stack gap="xs">
            <Title order={3}>Select Topic</Title>
            <Text>Select at least one topic for this role.</Text>
            <Stack gap={0} mah={rem(400)} style={{ overflow: "auto" }}>
                <TopicNode
                    topic={rootTopic}
                    checkedNodes={selectedCheckboxes}
                    checkboxOnChange={(topic, checked) => {
                        if (checked) setSelectedCheckboxes(selectedCheckboxes.concat(topic.id));
                        else setSelectedCheckboxes(selectedCheckboxes.filter((t) => t != topic.id));
                    }}
                    inherited={false}
                />
            </Stack>
            <Button
                w="100%"
                disabled={selectedCheckboxes.length == 0}
                onClick={() => {
                    handleRoleAssign(
                        userIds.flatMap((id) =>
                            selectedCheckboxes.map((topicId) =>
                                assignTopicRole
                                    .mutateAsync({ userId: id, roleId: selectedRoleId, topicId })
                                    .then((res) => res.assignTopicRole.user as StrictUndef<FullUserFragment>),
                            ),
                        ),
                    ).catch(logger.error);
                }}
            >
                Save
            </Button>
        </Stack>
    );
};

export const TopicEditPanel = ({
    user,
    selectedRoleId,
    initialTopicIds,
}: {
    user: UserSchema;
    selectedRoleId: string;
    initialTopicIds: string[];
}) => {
    const {
        data: { rootTopic },
    } = useSuspenseQuery(topicsQueryOptions.theWholeTree());

    const assignTopicRole = useGraphqlMutation(muts.assignTopicRole);
    const unassignRole = useGraphqlMutation(muts.unassignRole);

    const [selectedTopics, setSelectedTopics] = useState(initialTopicIds);

    const topicDiff = useMap<string, "add" | "remove">();

    return (
        <Stack gap="xs">
            <Title order={3}>Select Topic</Title>
            <Text>Select at least one topic for this role.</Text>
            <Stack gap={0} mah="400" style={{ overflow: "auto" }}>
                <TopicNode
                    topic={rootTopic}
                    checkedNodes={selectedTopics}
                    checkboxOnChange={(topic, checked) => {
                        if (checked) {
                            setSelectedTopics(selectedTopics.concat(topic.id));
                            if (!initialTopicIds.includes(topic.id)) topicDiff.set(topic.id, "add");
                            else if (topicDiff.has(topic.id)) topicDiff.delete(topic.id);
                        } else {
                            setSelectedTopics(selectedTopics.filter((it) => it != topic.id));
                            if (initialTopicIds.includes(topic.id)) topicDiff.set(topic.id, "remove");
                            else if (topicDiff.has(topic.id)) topicDiff.delete(topic.id);
                        }
                    }}
                    inherited={false}
                />
            </Stack>
            <Button
                w="100%"
                disabled={topicDiff.size == 0}
                onClick={() => {
                    const promises = Array.from(topicDiff).map(([id, op]) => {
                        if (op == "add")
                            return assignTopicRole
                                .mutateAsync({ userId: user.id, roleId: selectedRoleId, topicId: id })
                                .then((res) => res.assignTopicRole.user as StrictUndef<FullUserFragment>);
                        else {
                            const userRoleId = user.roles.find((r) => r.role.id == selectedRoleId && r.topic?.id == id)?.id;
                            if (!userRoleId)
                                return Promise.reject(`userRoleId not found on user ${user.id} for role ${selectedRoleId} for topic ${id}`);
                            else
                                return unassignRole
                                    .mutateAsync({ userId: user.id, userRoleId: userRoleId! })
                                    .then((res) => res.unassignRole as StrictUndef<FullUserFragment>);
                        }
                    });

                    handleRoleAssign(promises).catch(logger.error);
                }}
            >
                Save
            </Button>
        </Stack>
    );
};

const muts = {
    assignTopicRole: graphql(`
        mutation TopicRoleManageAssignTopicRole($userId: UUID!, $roleId: UUID!, $topicId: UUID!) {
            assignTopicRole(params: { roleId: $roleId, userId: $userId, topicId: $topicId }) {
                user {
                    ...FullUser
                }
            }
        }
    `),
    unassignRole: graphql(`
        mutation TopicRoleManageUnassignRole($userId: UUID!, $userRoleId: UUID!) {
            unassignRole(userId: $userId, userRoleId: $userRoleId) {
                ...FullUser
            }
        }
    `),
};
